import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Card, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/iconify';
import { InvitationAction } from './useInvitationDecisions';

interface Props {
  state: 'error' | 'empty';
  action: InvitationAction | null;
  actionError: boolean;
  onRetry: () => void;
  onDashboard: () => void;
}

/** Read failure and no-pending-invitations states. */
export default function InvitationStatusCard({
  state,
  action,
  actionError,
  onRetry,
  onDashboard,
}: Props): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card
      sx={(theme) => ({
        width: 1,
        maxWidth: theme.spacing(70),
        mx: 'auto',
        p: { xs: 3, md: 5 },
        borderRadius: 4,
        boxShadow: theme.customShadows.z8,
        bgcolor: 'background.default',
      })}
    >
      {state === 'error' ? (
        <Stack spacing={3}>
          <Alert severity="error">{t('invitationAcceptance.loadError')}</Alert>
          <Button variant="outlined" color="inherit" onClick={onRetry}>
            {t('invitationAcceptance.retry')}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={2} alignItems="flex-start">
          <Box
            aria-hidden
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'text.primary',
              color: 'secondary.main',
            }}
          >
            <Iconify icon="eva:checkmark-circle-2-outline" width={28} />
          </Box>
          <Typography variant="h4" component="h1">
            {t('invitationAcceptance.empty')}
          </Typography>
          <Typography color="text.secondary">
            {t('invitationAcceptance.emptyDescription')}
          </Typography>
          {actionError && (
            <Alert severity="error" sx={{ width: 1 }}>
              {t('invitationAcceptance.actionError')}
            </Alert>
          )}
          <LoadingButton
            variant="contained"
            size="large"
            loading={action === 'dashboard'}
            onClick={onDashboard}
            sx={{
              bgcolor: 'text.primary',
              color: 'background.default',
              '&:hover': { bgcolor: 'text.primary' },
            }}
          >
            {t('invitationAcceptance.dashboard')}
          </LoadingButton>
        </Stack>
      )}
    </Card>
  );
}
